import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Bell,
  Loader2,
  X,
  ChevronDown,
  ExternalLink,
  Clock,
  Tag,
  Globe,
  Check
} from 'lucide-react';
import UniversalTable from '../../services/universalTable';
import { resolveRowLimit, useRowLimitOverride } from '../../services/planRowLimit';

const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

const GlobalAffiliateFound = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const notificationIdFromUrl = searchParams.get('id');

  // State
  const [filteredData, setFilteredData] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [isLoadingTableData, setIsLoadingTableData] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [userPlan, setUserPlan] = useState('Free');
  const [notifications, setNotifications] = useState([]);
  const [selectedNotificationId, setSelectedNotificationId] = useState(notificationIdFromUrl ? parseInt(notificationIdFromUrl) : null);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [currentFilters, setCurrentFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortByLatest, setSortByLatest] = useState(false);
  const [showNotificationPicker, setShowNotificationPicker] = useState(false);

  // Rows past the plan ceiling get a blur placeholder instead of the real
  // screenshot. The ceiling is the plan default (Free 0 / Growth 25 / Pro 500 /
  // Enterprise unlimited) unless users.row_limit overrides it for this account.
  // See services/planRowLimit.js.
  const rowLimitOverride = useRowLimitOverride();
  const getPlanImageLimit = (plan) => resolveRowLimit(plan, rowLimitOverride);

  const fetchUserInfo = async () => {
    try {
      const response = await fetch(`${basePath}/api/planfetch`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const userData = await response.json();
        if (userData.plan) setUserPlan(userData.plan);
      }
    } catch (error) {
      console.error('Error fetching user plan:', error);
      setUserPlan('Free');
    }
  };

  const TransformDataAPI = (data) => {
    const imageLimit = getPlanImageLimit(userPlan);

    return data.map((item, index) => {
      // Real screenshot inside the ceiling, blur placeholder past it.
      const imageUrl = index < imageLimit
        ? (item.screenshot_url || item.imageUrl || 'blurrow.png')
        : 'blurrow.png';

      return {
        paid_traffic: item.adwords_traffic || item.paid_traffic,
        imageUrl,
        paid_traffic_difference: item.adwords_traffic_difference || item.paid_traffic_difference,
        affiliate_url: item.website_url || item.affiliate_url,
        already_approached: item.already_approached,
        email: item.email,
        id: item.id || item.affiliate_id,
        keywords: item.keywords,
        organic_traffic: item.organic_traffic,
        organic_traffic_difference: item.organic_traffic_difference,
        paid_keyword: item.paid_keyword,
        adwords_keywords: item.adwords_keywords,
        paid_top_page: item.paid_top_page,
        rank: item.rank,
        rank_difference: item.rank_difference,
        top_geo_organic_traffic: item.top_geo_organic_traffic,
        top_geo_organic_traffic_difference: item.top_geo_organic_traffic_difference,
        top_page: item.top_page,
        total_traffic: item.total_traffic,
        total_traffic_difference: item.total_traffic_difference,
        screenshot_url: item.screenshot_url,
        vertical: item.vertical,
        affiliate_url_confidence: item.affiliate_url_confidence
      };
    });
  };

  const fetchDataWithPagination = async (page = 0, pageSize = currentPageSize, filters = null, notifId = null) => {
    setIsLoadingTableData(true);
    setApiError(null);

    const filtersToUse = filters !== null ? filters : currentFilters;
    const offset = page * pageSize;
    const notificationIdToUse = notifId !== null ? notifId : selectedNotificationId;

    const requestBody = {
      ...filtersToUse,
      page,
      pageSize,
      offset,
      search: filtersToUse.search || searchQuery || '',
      notificationId: notificationIdToUse || undefined
    };

    try {
      const response = await fetch(`${basePath}/api/fetchNotification`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const responseData = await response.json();

      if (responseData.success) {
        if (responseData.data?.user?.plan) setUserPlan(responseData.data.user.plan);

        // Store all notifications (always fetch all for the picker)
        if (responseData.data.notifications) {
          setNotifications(responseData.data.notifications);
        }

        if (responseData.data && Array.isArray(responseData.data.data)) {
          const transformedData = TransformDataAPI(responseData.data.data);
          setFilteredData(transformedData);

          setPaginationInfo({
            page: responseData.data.page || page,
            pageSize: responseData.data.pageSize || pageSize,
            total: responseData.data.total || transformedData.length,
            totalPages: responseData.data.totalPages || Math.ceil((responseData.data.total || transformedData.length) / pageSize),
            hasNextPage: responseData.data.hasNextPage || false,
            hasPreviousPage: responseData.data.hasPreviousPage || page > 0,
            offset
          });
        } else {
          setFilteredData([]);
        }
      } else {
        throw new Error(responseData.message || 'No data received');
      }
    } catch (error) {
      console.error('Error fetching notification data:', error);
      setApiError(error.message);
      setFilteredData([]);
    } finally {
      setIsLoadingTableData(false);
    }
  };

  // Also fetch all notifications list for the picker (without filtering)
  const fetchAllNotifications = async () => {
    try {
      const response = await fetch(`${basePath}/api/fetchNotification`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: 0, pageSize: 1 })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.notifications) {
          setNotifications(data.data.notifications);
        }
      }
    } catch (error) {
      console.error('Error fetching notifications list:', error);
    }
  };

  const handlePageChange = async (newPage) => {
    try {
      setPaginationInfo(prev => prev ? { ...prev, page: newPage } : null);
      await fetchDataWithPagination(newPage, currentPageSize, currentFilters);
    } catch (error) {
      console.error('Error during page change:', error);
      setApiError('Failed to load page data');
    }
  };

  const handlePageSizeChange = async (newPageSize) => {
    setCurrentPageSize(newPageSize);
    try {
      setPaginationInfo(prev => prev ? { ...prev, page: 0, pageSize: newPageSize, totalPages: Math.ceil((prev.total || 0) / newPageSize) } : null);
      await fetchDataWithPagination(0, newPageSize, currentFilters);
    } catch (error) {
      console.error('Error during page size change:', error);
      setCurrentPageSize(paginationInfo?.pageSize || 10);
    }
  };

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    const filters = { ...currentFilters, search: query };
    setCurrentFilters(filters);
    fetchDataWithPagination(0, currentPageSize, filters);
  }, [currentFilters, currentPageSize, selectedNotificationId]);

  const handleSortByLatestChange = (newValue) => {
    setSortByLatest(newValue);
    const filters = { ...currentFilters, sortByLatest: newValue, search: searchQuery };
    setCurrentFilters(filters);
    fetchDataWithPagination(0, currentPageSize, filters);
  };

  const handleNotificationSelect = (notifId) => {
    setSelectedNotificationId(notifId);
    setShowNotificationPicker(false);
    setSearchQuery('');
    setCurrentFilters({});
    setSortByLatest(false);

    // Update URL
    if (notifId) {
      setSearchParams({ id: notifId });
    } else {
      setSearchParams({});
    }

    fetchDataWithPagination(0, currentPageSize, {}, notifId);
  };

  useEffect(() => {
    fetchUserInfo();
    fetchAllNotifications();
    fetchDataWithPagination(0, currentPageSize, {});
  }, []);

  useEffect(() => {
    if (filteredData.length > 0) {
      const transformedData = TransformDataAPI(filteredData.map(item => ({ ...item, screenshot_url: item.screenshot_url })));
      setFilteredData(transformedData);
    }
  }, [userPlan]);

  // Get current notification details for header display
  const currentNotification = selectedNotificationId
    ? notifications.find(n => n.id === selectedNotificationId)
    : null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950">

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <Bell className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                {currentNotification ? 'Notification Affiliates' : 'All Notification Affiliates'}
              </h1>
              <p className="text-gray-600 dark:text-slate-400 leading-relaxed max-w-xl">
                {currentNotification
                  ? `Affiliates discovered from ${currentNotification.trigger_brand_name || 'brand addition'}`
                  : 'Browse all affiliates discovered through notifications'
                }
              </p>
            </div>

            {/* Notification Picker */}
            {notifications.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowNotificationPicker(!showNotificationPicker)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg hover:border-blue-400 transition-all text-sm font-medium text-gray-700 dark:text-slate-300"
                >
                  <Bell className="w-4 h-4" />
                  <span>{currentNotification ? `Notification #${currentNotification.id}` : 'All Notifications'}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showNotificationPicker ? 'rotate-180' : ''}`} />
                </button>

                {showNotificationPicker && (
                  <div className="absolute right-0 z-50 w-96 mt-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden">
                    {/* All Notifications option */}
                    <button
                      onClick={() => handleNotificationSelect(null)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors border-b border-gray-100 dark:border-slate-800 ${
                        !selectedNotificationId ? 'bg-blue-50 dark:bg-slate-800' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                        <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">All Notifications</p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">{notifications.length} notification{notifications.length !== 1 ? 's' : ''}</p>
                      </div>
                      {!selectedNotificationId && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                    </button>

                    {/* Individual notifications */}
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.map((notif) => {
                        const notifData = notif.notification_data || {};
                        const verticals = notif.matching_verticals ? notif.matching_verticals.split(',').filter(Boolean) : [];
                        const countries = notifData.countries || [];
                        const isSelected = selectedNotificationId === notif.id;

                        return (
                          <button
                            key={notif.id}
                            onClick={() => handleNotificationSelect(notif.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors border-b border-gray-50 dark:border-slate-800 last:border-0 ${
                              isSelected ? 'bg-blue-50 dark:bg-slate-800' : ''
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              notif.is_read
                                ? 'bg-gray-100 dark:bg-slate-700'
                                : 'bg-blue-100 dark:bg-blue-900/30'
                            }`}>
                              <Bell className={`w-4 h-4 ${notif.is_read ? 'text-gray-400' : 'text-blue-600 dark:text-blue-400'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {notif.trigger_brand_name || 'Unknown Brand'}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-gray-500 dark:text-slate-400">
                                  {notif.urls_count || notifData.new_urls_count || 0} affiliates
                                </span>
                                {verticals.length > 0 && (
                                  <>
                                    <span className="text-gray-300 dark:text-slate-600">·</span>
                                    <span className="text-xs text-indigo-600 dark:text-indigo-400 truncate">
                                      {verticals.slice(0, 2).join(', ')}
                                    </span>
                                  </>
                                )}
                                {countries.length > 0 && (
                                  <>
                                    <span className="text-gray-300 dark:text-slate-600">·</span>
                                    <span className="text-xs text-emerald-600 dark:text-emerald-400 truncate">
                                      {countries.slice(0, 2).join(', ')}{countries.length > 2 ? ` +${countries.length - 2}` : ''}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                              <span className="text-xs text-gray-400 dark:text-slate-500">{formatDate(notif.created_at)}</span>
                              {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notification details banner */}
          {currentNotification && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-800">
              <div className="flex items-center gap-4 flex-wrap">
                {/* Trigger brand */}
                {currentNotification.trigger_brand_name && (
                  <div className="flex items-center gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-slate-400">Source:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{currentNotification.trigger_brand_name}</span>
                  </div>
                )}

                {/* Verticals */}
                {currentNotification.matching_verticals && (
                  <div className="flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5 text-gray-400" />
                    {currentNotification.matching_verticals.split(',').filter(Boolean).map((v, i) => (
                      <span key={i} className="inline-block px-2 py-0.5 text-xs font-medium bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded-full border border-indigo-100 dark:border-indigo-800">
                        {v.trim()}
                      </span>
                    ))}
                  </div>
                )}

                {/* Countries */}
                {currentNotification.notification_data?.countries?.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Globe className="w-3.5 h-3.5 text-gray-400" />
                    {currentNotification.notification_data.countries.slice(0, 3).map((c, i) => (
                      <span key={i} className="inline-block px-2 py-0.5 text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 rounded-full border border-emerald-100 dark:border-emerald-800">
                        {c}
                      </span>
                    ))}
                    {currentNotification.notification_data.countries.length > 3 && (
                      <span className="text-xs text-gray-500 dark:text-slate-400">
                        +{currentNotification.notification_data.countries.length - 3} more
                      </span>
                    )}
                  </div>
                )}

                {/* Date */}
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs text-gray-500 dark:text-slate-400">{formatDate(currentNotification.created_at)}</span>
                </div>

                {/* Affiliates count */}
                <span className="text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full border border-blue-100 dark:border-blue-800">
                  {currentNotification.urls_count || currentNotification.notification_data?.new_urls_count || 0} affiliates
                </span>

                {/* Clear filter */}
                <button
                  onClick={() => handleNotificationSelect(null)}
                  className="ml-auto flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                  Show all
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      {apiError && (
        <div className="mb-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 rounded-lg p-4">
            <div className="flex items-center">
              <X className="h-5 w-5 text-red-400 flex-shrink-0" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-400">Error</h3>
                <div className="mt-1 text-sm text-red-700 dark:text-red-400">{apiError}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Table - same as affiliate directory */}
      {filteredData.length > 0 || isLoadingTableData ? (
        <UniversalTable
          data={filteredData}
          paginationInfo={paginationInfo}
          onFetchData={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          title={currentNotification ? `Affiliates from ${currentNotification.trigger_brand_name || 'Notification'}` : 'All Notification Affiliates'}
          searchable={true}
          defaultPageSize={currentPageSize}
          loading={isLoadingTableData}
          userPlan={userPlan}
          expandable={true}
          sortByLatest={sortByLatest}
          onSortByLatestChange={handleSortByLatestChange}
        />
      ) : null}

      {/* Loading */}
      {filteredData.length === 0 && isLoadingTableData && !paginationInfo && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600 dark:text-slate-400">Loading notification data...</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {filteredData.length === 0 && !isLoadingTableData && !apiError && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-8">
          <div className="text-center text-gray-500 dark:text-slate-400">
            <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-slate-600" />
            <h3 className="text-lg font-medium mb-2 text-gray-800 dark:text-slate-100">No Affiliate Data Found</h3>
            <p>No affiliate data from notifications is currently available.</p>
            {selectedNotificationId && (
              <button
                onClick={() => handleNotificationSelect(null)}
                className="mt-4 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              >
                View all notifications
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalAffiliateFound;
